import { ICSPRClickSDK } from '@make-software/csprclick-core-types/isdk';

import { Message, StampAction } from '@/entities';

import { ApiError, PaginatedResponse } from './types';
import {
  fromNFTActionToMessage,
  fromTransactionToMessage,
  fromFTActionToStampAction,
} from './data-mappers';

export class ApiClient {
  #clickRef: ICSPRClickSDK;

  constructor(clickRef: ICSPRClickSDK) {
    this.#clickRef = clickRef;
  }

  async getAllMessages(page: number = 1, pageSize: number = 10): Promise<PaginatedResponse<Message>> {
    const response = await this.#fetch(
      `/contract-packages/${config.message_token_contract_package_hash}/nft-token-actions?` + [
        'includes=deploy',
        `page=${page}`,
        `page_size=${pageSize}`,
      ].join('&')
    );

    return {
      data: response.data.map(fromNFTActionToMessage),
      itemCount: response.item_count,
      pageCount: response.page_count
    };
  }

  async getMessagesReceivedBy(publicKey?: string, page: number = 1, pageSize: number = 10): Promise<PaginatedResponse<Message>> {
    if (!publicKey) {
      return {
        data: [],
        itemCount: 0,
        pageCount: 0
      };
    }

    const response = await this.#fetch(
      `/accounts/${publicKey}/nft-token-actions?` + [
        'includes=deploy',
        `contract_package_hash=${config.message_token_contract_package_hash}`,
        `page=${page}`,
        `page_size=${pageSize}`,
      ].join('&')
    );

    return {
      data: response.data.map(fromNFTActionToMessage),
      itemCount: response.item_count,
      pageCount: response.page_count
    };
  }

  async getMessagesSentBy(publicKey?: string, page: number = 1, pageSize: number = 10): Promise<PaginatedResponse<Message>> {
    if (!publicKey) {
      return {
        data: [],
        itemCount: 0,
        pageCount: 0
      };
    }

    const response = await this.#fetch(
      `/accounts/${publicKey}/deploys?` + [
        'includes=nft_token_actions',
        `contract_entrypoint_id=${config.send_message_entry_point_id}`,
        `page=${page}`,
        `page_size=${pageSize}`,
      ].join('&')
    );

    return {
      data: response.data.map(fromTransactionToMessage),
      itemCount: response.item_count,
      pageCount: response.page_count
    };
  }

  async getMessage(id?: number): Promise<Message|null> {
    if (!id) {
      return null;
    }

    const response = await this.#fetch(
      `/contract-packages/${config.message_token_contract_package_hash}/nft-tokens/${id}/nft-token-actions?` + [
        'includes=deploy',
        `page=1`,
        `page_size=1`,
      ].join('&')
    );

    if (response.data.length === 0) {
      return null;
    }

    return fromNFTActionToMessage(response.data[0]);
  }

  async getStampHistoryFor(publicKey: string, page: number = 1, pageSize: number = 10): Promise<PaginatedResponse<StampAction>> {
    const response = await this.#fetch(
      `/accounts/${publicKey}/ft-token-actions?` + [
        'includes=deploy,from_public_key,to_public_key',
        `contract_package_hash=${config.stamp_token_contract_package_hash}`,
        `page=${page}`,
        `page_size=${pageSize}`,
      ].join('&')
    );

    return {
      data: response.data.map(fromFTActionToStampAction),
      itemCount: response.item_count,
      pageCount: response.page_count
    };
  }

  async getStampBalanceFor(publicKey?: string): Promise<number> {
    if (!publicKey) {
      return 0;
    }

    const response = await this.#fetch(
      `/accounts/${publicKey}/ft-token-ownership?` + [
        `contract_package_hash=${config.stamp_token_contract_package_hash}`,
        `page=1`,
        `page_size=1`,
      ].join('&')
    );

    return response.data.length > 0
      ? parseInt(response.data[0].balance)
      : 0;
  }

  async #fetch(query: string) {
    const csprCloudProxy = this.#clickRef.getCsprCloudProxy();

    const csprClickBaseUrl = csprCloudProxy.RpcURL.replace('/node-proxy/rpc', '');
    const trimmedQuery = query.replace(/^\/+/, '');

    const response = await fetch(`${csprClickBaseUrl}/cloud-proxy/${trimmedQuery}`, {
      method: 'GET',
      headers: {
        Authorization: csprCloudProxy.RpcDigestToken,
      }
    });

    const decodedResponse = await response.json();
    if ('error' in decodedResponse) {
      throw new ApiError(
        'code' in decodedResponse.error ? decodedResponse.error.code : '500',
        'message' in decodedResponse.error ? decodedResponse.error.message : 'API error',
        'description' in decodedResponse.error ? decodedResponse.error.description : '',
      );
    }

    return decodedResponse;
  }
}
